import type { ParameterDef, ScheduleParameterValues, ScheduleParameterEntry } from "@/types";

/**
 * Reconcile stored schedule parameters with the current plugin parameter definitions.
 *
 * - Includes all parameters from pluginParams in the output, using stored values
 *   where available and defaults otherwise.
 * - Excludes any stored parameters whose name does not appear in the current
 *   plugin definition (i.e. parameters removed from the flow).
 *
 * For date_range type parameters without a stored value, defaults to dynamic mode
 * with a 7-day offset. For all other types, defaults to static mode with the
 * parameter's configured default value (or null).
 */
export function reconcileParameters(
  pluginParams: ParameterDef[],
  storedParams: ScheduleParameterValues
): ScheduleParameterValues {
  const result: ScheduleParameterValues = {};

  for (const param of pluginParams) {
    const stored = storedParams[param.name];
    if (stored !== undefined) {
      // Use the stored entry as-is
      result[param.name] = stored;
    } else {
      // Create a default entry based on parameter type
      result[param.name] = buildDefaultEntry(param);
    }
  }

  return result;
}

/**
 * Build a default ScheduleParameterEntry for a parameter definition.
 * date_range types default to dynamic mode with a 7-day offset.
 * All other types default to static mode with the param's default value or null.
 */
function buildDefaultEntry(param: ParameterDef): ScheduleParameterEntry {
  if (param.type === "param_date_range") {
    return {
      value: null,
      mode: "dynamic",
      offset: { amount: 7, unit: "days" },
    };
  }

  return {
    value: param.default ?? null,
    mode: "static",
  };
}
